import { hasValue } from "./data";
import { diasUteis } from "./calculos";

export const DEADLINE_STATUS_LABELS = {
  overdue: "Vencido",
  today: "Vence hoje",
  thisWeek: "Vence essa semana",
  atRisk: "Em risco",
  future: "Dentro do prazo",
};

function buildLocalDate(year, month, day) {
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function parseSlashDate(rawValue) {
  const slashMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:$|\s)/);

  if (!slashMatch) {
    return null;
  }

  const [, firstPart, secondPart, rawYear] = slashMatch;
  const parsedYear = Number(rawYear);
  const year = rawYear.length === 2 ? 2000 + parsedYear : parsedYear;
  const first = Number(firstPart);
  const second = Number(secondPart);

  if (first > 12) {
    return buildLocalDate(year, second, first);
  }

  if (second > 12) {
    return buildLocalDate(year, first, second);
  }

  return buildLocalDate(year, second, first) || buildLocalDate(year, first, second);
}

export function toDate(value) {
  if (!hasValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const rawValue = String(value).trim();
  const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return buildLocalDate(Number(year), Number(month), Number(day));
  }

  const slashDate = parseSlashDate(rawValue);

  if (slashDate) {
    return slashDate;
  }

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(value = new Date()) {
  const date = toDate(value);

  if (!date) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

export function isDueToday(value, referenceDate = new Date()) {
  const date = startOfDay(value);
  const today = startOfDay(referenceDate);

  return Boolean(date && today && date.getTime() === today.getTime());
}

export function endOfWeek(value = new Date()) {
  const date = startOfDay(value);

  if (!date) {
    return null;
  }

  const day = date.getDay();
  const distanceFromMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - distanceFromMonday + 6);
  date.setHours(23, 59, 59, 999);

  return date;
}

export function isBeforeToday(value, referenceDate = new Date()) {
  const date = toDate(value);
  const today = startOfDay(referenceDate);

  return Boolean(date && today && date < today);
}

export function isDueThisWeek(value, referenceDate = new Date()) {
  const date = toDate(value);
  const today = startOfDay(referenceDate);
  const weekEnd = endOfWeek(referenceDate);

  return Boolean(date && today && weekEnd && date > today && date <= weekEnd);
}

export function getDeadlineBucket(value, referenceDate = new Date()) {
  if (isBeforeToday(value, referenceDate)) {
    return "overdue";
  }

  if (isDueToday(value, referenceDate)) {
    return "today";
  }

  if (isDueThisWeek(value, referenceDate)) {
    return "this_week";
  }

  return toDate(value) ? "future" : "none";
}

export function getDeadlineStatusVariant(
  value,
  { fallback = "success", referenceDate = new Date() } = {}
) {
  const bucket = getDeadlineBucket(value, referenceDate);

  if (bucket === "overdue") {
    return "danger";
  }

  if (bucket === "today") {
    return "today";
  }

  if (bucket === "this_week") {
    return "warning";
  }

  return fallback;
}

export function buildDeadlineStatus(
  value,
  {
    overdueLabel = DEADLINE_STATUS_LABELS.overdue,
    todayLabel = DEADLINE_STATUS_LABELS.today,
    warningLabel = DEADLINE_STATUS_LABELS.thisWeek,
    fallbackLabel = DEADLINE_STATUS_LABELS.future,
    fallbackVariant = "success",
    referenceDate = new Date(),
  } = {}
) {
  const bucket = getDeadlineBucket(value, referenceDate);
  const variant = getDeadlineStatusVariant(value, {
    fallback: fallbackVariant,
    referenceDate,
  });

  if (bucket === "overdue") {
    return { status: overdueLabel, statusVariant: variant };
  }

  if (bucket === "today") {
    return { status: todayLabel, statusVariant: variant };
  }

  if (bucket === "this_week") {
    return { status: warningLabel, statusVariant: variant };
  }

  return { status: fallbackLabel, statusVariant: variant };
}

// D-031: classifica em "Em risco" quando faltam menos dias uteis ate a
// data limite do que o lead time tipico da etapa atual. Permite ao
// gestor agir ANTES do vencimento, em vez de so reagir ao "Vencido".
export function isAtRisk(value, leadTimeUteis = 5, referenceDate = new Date()) {
  const date = toDate(value);
  const today = startOfDay(referenceDate);
  if (!date || !today) return false;
  if (date <= today) return false; // ja vencido ou vence hoje
  const dias = diasUteis(today, date);
  if (!Number.isFinite(dias)) return false;
  return dias <= Math.max(1, Number(leadTimeUteis) || 5);
}

export function buildPreventiveStatus(
  value,
  {
    leadTimeUteis = 5,
    overdueLabel = DEADLINE_STATUS_LABELS.overdue,
    todayLabel = DEADLINE_STATUS_LABELS.today,
    atRiskLabel = DEADLINE_STATUS_LABELS.atRisk,
    fallbackLabel = DEADLINE_STATUS_LABELS.future,
    referenceDate = new Date(),
  } = {}
) {
  if (!toDate(value)) {
    return { status: "Sem prazo definido", statusVariant: "default" };
  }
  if (isBeforeToday(value, referenceDate)) {
    return { status: overdueLabel, statusVariant: "danger" };
  }
  if (isDueToday(value, referenceDate)) {
    return { status: todayLabel, statusVariant: "today" };
  }
  if (isAtRisk(value, leadTimeUteis, referenceDate)) {
    return { status: atRiskLabel, statusVariant: "warning" };
  }
  return { status: fallbackLabel, statusVariant: "success" };
}

export function compareDatesAsc(a, b) {
  const left = toDate(a);
  const right = toDate(b);

  return (left ? left.getTime() : Number.POSITIVE_INFINITY) -
    (right ? right.getTime() : Number.POSITIVE_INFINITY);
}

export function compareDatesDesc(a, b) {
  return compareDatesAsc(b, a);
}

export function inferStatusVariant(status) {
  const normalized = String(status ?? "").toLowerCase();

  if (normalized.includes("vencido") || normalized.includes("atraso")) {
    return "danger";
  }

  if (normalized.includes("vence hoje")) {
    return "today";
  }

  if (normalized.includes("em risco")) {
    return "warning";
  }

  if (normalized.includes("vence essa semana")) {
    return "warning";
  }

  if (
    normalized.includes("aguardando") ||
    normalized.includes("prazo") ||
    normalized.includes("dentro do prazo") ||
    normalized.includes("produção") ||
    normalized.includes("producao") ||
    normalized.includes("entregue")
  ) {
    return "success";
  }

  if (normalized.includes("não calculado") || normalized.includes("não calculado")) {
    return "warning";
  }

  return "default";
}
